function pickString(payload: Record<string, unknown>, keys: Array<string>): string {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function stringifyMarkdownCell(value: unknown): string {
  let text = '';
  if (typeof value === 'string') {
    text = value;
  } else if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    text = String(value);
  } else if (value && typeof value === 'object') {
    text = JSON.stringify(value);
  }

  return text.replace(/\|/g, '\\|').replace(/\n/g, '<br>');
}

function extractColumnNames(columnsRaw: unknown): Array<string> {
  if (!Array.isArray(columnsRaw)) {
    return [];
  }

  return columnsRaw
    .map((column) => {
      if (typeof column === 'string') {
        return column.trim();
      }
      if (column && typeof column === 'object') {
        const obj = column as Record<string, unknown>;
        const field = obj.field;
        const headerName = obj.headerName;
        if (typeof field === 'string' && field.length > 0) {
          return field;
        }
        if (typeof headerName === 'string' && headerName.length > 0) {
          return headerName;
        }
      }
      return '';
    })
    .filter((header) => header.length > 0);
}

export function pickTableFromBlock(payload: Record<string, unknown>): {
  headers: Array<string>;
  rows: Array<Array<unknown>>;
} {
  const columnsRaw = payload.columns;
  const rowsRaw = payload.rows;

  if (!Array.isArray(rowsRaw)) {
    const headers = extractColumnNames(columnsRaw);
    return { headers, rows: [] };
  }

  let headers = extractColumnNames(columnsRaw);

  if (rowsRaw.length === 0) {
    return { headers, rows: [] };
  }

  if (Array.isArray(rowsRaw[0])) {
    const tuples = rowsRaw as Array<Array<unknown>>;
    return { headers, rows: tuples.map((row) => Array.from(row)) };
  }

  const objectRows = rowsRaw as Array<Record<string, unknown>>;
  if (headers.length === 0) {
    headers = Object.keys(objectRows[0] ?? {});
  }
  const rows = objectRows.map((row) => headers.map((header) => row[header]));
  return { headers, rows };
}

export function normalizeTableBlock(payload: Record<string, unknown>): {
  headers: Array<string>;
  rows: Array<Array<unknown>>;
  dataRows: Array<Record<string, unknown>>;
  columnsForGrid: unknown;
  rowCount: number;
  /** True total row count of the result (may exceed the inline rows when truncated). */
  totalRowCount: number;
  columnCount: number;
  canDownload: boolean;
  jobId?: string;
  truncated: boolean;
} {
  const columnsRaw = payload.columns;
  const rowsRaw = payload.rows;
  const { headers, rows } = pickTableFromBlock(payload);
  let dataRows: Array<Record<string, unknown>> = [];
  let columnsForGrid: unknown = columnsRaw;

  if (Array.isArray(rowsRaw) && rowsRaw.length > 0) {
    if (Array.isArray(rowsRaw[0])) {
      const tuples = rowsRaw as Array<Array<unknown>>;
      if (headers.length > 0) {
        dataRows = tuples.map((tuple) => {
          const row: Record<string, unknown> = {};
          headers.forEach((header, index) => {
            row[header] = tuple[index];
          });
          return row;
        });
        columnsForGrid = headers;
      } else {
        dataRows = tuples.map((tuple) => {
          const row: Record<string, unknown> = {};
          tuple.forEach((value, index) => {
            row[String(index)] = value;
          });
          return row;
        });
        columnsForGrid = Object.keys(dataRows[0] ?? {});
      }
    } else {
      dataRows = rowsRaw as Array<Record<string, unknown>>;
    }
  }

  const firstTupleColumnCount = Array.isArray(rowsRaw) && Array.isArray(rowsRaw[0]) ? rowsRaw[0].length : 0;
  const fallbackColumnCount = Object.keys(dataRows[0] ?? {}).length;
  const columnCount = headers.length || firstTupleColumnCount || fallbackColumnCount;

  return {
    headers,
    rows,
    dataRows,
    columnsForGrid,
    rowCount: rows.length,
    // le-data sends the true total in payload.rowCount; the inline rows may be a capped preview.
    totalRowCount: typeof payload.rowCount === 'number' ? Math.max(payload.rowCount, rows.length) : rows.length,
    columnCount,
    canDownload: headers.length > 0 && dataRows.length > 0,
    jobId: typeof payload.jobId === 'string' ? payload.jobId : undefined,
    // Prefer le-data's explicit flag; fall back to the exact-cap heuristic for older blocks.
    truncated: payload.truncated === true || rows.length >= SERVER_ROW_CAP,
  };
}

/**
 * le-data truncates result sets server-side at this many rows. There is no explicit
 * truncation flag on the block payload (see `LeDataContentBlock` in types/leData.ts), so
 * an exact hit on the cap is the only honest signal that rows were dropped.
 */
export const SERVER_ROW_CAP = 1000;

/**
 * Heuristic for "this table was probably cut off by the server row cap". A result that lands
 * on exactly {@link SERVER_ROW_CAP} rows is almost certainly truncated rather than naturally
 * that size, so callers can surface a data-honesty caption. Kept pure for unit testing.
 */
export function isLikelyRowCapped(rowCount: number): boolean {
  return rowCount === SERVER_ROW_CAP;
}

export function formatTableBlockAsMarkdown(payload: Record<string, unknown>): string {
  const markdown = pickString(payload, ['markdown', 'md']);
  if (markdown) {
    return markdown;
  }

  const { headers, rows } = pickTableFromBlock(payload);
  if (headers.length === 0) {
    return '';
  }

  return [
    `| ${headers.map(stringifyMarkdownCell).join(' | ')} |`,
    `| ${headers.map(() => '---').join(' | ')} |`,
    ...rows.map((row) => `| ${row.map(stringifyMarkdownCell).join(' | ')} |`),
  ].join('\n');
}
